import {Action, Selector, State, StateContext} from '@ngxs/store';
import {tap} from 'rxjs/operators';
import {Partner, PartnerIdentity} from '../../models/partner.model';
import {FormState} from '../../models/form-state.model';
import {PartnerService} from '../../services/partner.service';
import {SearchList} from '../../models/search-list.model';
import {NavService} from '../../services/nav.service';
import {Injectable} from '@angular/core';
import {setFormStatus, setFormValidationErrors} from '../../helpers/form.helper';
import {handleFetchListPage} from '../../helpers/search.helper';
import {FormStatus} from '../../enums/file-status.enum';
import {NavItemCodes} from '../../enums/nav-item-codes.enum';
import {PartnerActions} from './partner.actions';


interface PartnerStateModel {
    form: FormState;
    list: SearchList<Partner>;
    activePartner: Partner;
    fieldList: PartnerIdentity[];
}

const STATE_NAME = 'partner';

@State<PartnerStateModel>({
    name: 'partner',
    defaults: {
        form: new FormState(),
        list: new SearchList<Partner>({
            filters: new FormState()
        }),
        activePartner: null,
        fieldList: null,
    }
})

@Injectable()
export class PartnerState {
    constructor(
        private navService: NavService,
        private partnerService: PartnerService
    ) {}

    @Selector()
    static partnerList(state) {
        return state.list;
    }

    @Selector()
    static activePartner(state) {
        return state.activePartner;
    }

    @Selector()
    static partnerFormErrors(state) {
        return state.form.validationErrors;
    }

    @Selector()
    static partnerFieldList(state) {
        return state.fieldList;
    }

    @Selector()
    static sortedPartnerFieldList(state) {
        return state.fieldList.slice().sort((a, b) => (a.nameShort > b.nameShort) ? 1 : -1);
    }

    @Action(PartnerActions.GetListPage)
    getListPage(ctx: StateContext<PartnerStateModel>, action: PartnerActions.GetListPage) {
        return handleFetchListPage(ctx, action.params, (props) => this.partnerService.fetchList(props));
    }

    @Action(PartnerActions.Activate)
    activatePartner(ctx: StateContext<PartnerStateModel>, {id}: PartnerActions.Activate) {
        return this.partnerService.activate(id).subscribe(
            () => ctx.dispatch(new PartnerActions.GetListPage())
        );
    }

    @Action(PartnerActions.Deactivate)
    deactivatePartner(ctx: StateContext<PartnerStateModel>, {id}: PartnerActions.Deactivate) {
        return this.partnerService.deactivate(id).subscribe(
            () => ctx.dispatch(new PartnerActions.GetListPage())
        );
    }

    @Action(PartnerActions.DeactivateSales)
    deactivatePartnerSales(ctx: StateContext<PartnerStateModel>, {id}: PartnerActions.DeactivateSales) {
        return this.partnerService.deactivateSales(id).subscribe(
            () => ctx.dispatch(new PartnerActions.GetListPage())
        );
    }

    @Action(PartnerActions.Add)
    addPartner(ctx: StateContext<PartnerStateModel>, {partner}: PartnerActions.Add) {
        setFormStatus(ctx, FormStatus.PENDING, STATE_NAME);
        return this.partnerService.add(partner).subscribe(
            () => this.navService.navigateByCode(NavItemCodes.PARTNER_MANAGEMENT),
            ({error}) => ctx.dispatch(new PartnerActions.FormFailure(error))
        );
    }

    @Action(PartnerActions.Update)
    updatePartner(ctx: StateContext<PartnerStateModel>, {partner}: PartnerActions.Update) {
        setFormStatus(ctx, FormStatus.PENDING, STATE_NAME);
        return this.partnerService.update(partner).subscribe(
            () => this.navService.navigateByCode(NavItemCodes.PARTNER_MANAGEMENT),
            ({error}) => ctx.dispatch(new PartnerActions.FormFailure(error))
        );
    }

    @Action(PartnerActions.FormFailure)
    addPartnerFailure(ctx: StateContext<PartnerStateModel>, {validationErrors = []}: PartnerActions.FormFailure) {
        setFormValidationErrors(ctx, validationErrors, STATE_NAME);
    }

    @Action(PartnerActions.GetById)
    getPartner(ctx: StateContext<PartnerStateModel>, {id}: PartnerActions.GetById) {
        return this.partnerService.fetchById(id).pipe(
            tap((partner) => {
                ctx.patchState({
                    activePartner: new Partner(partner)
                });
            })
        );
    }


    @Action(PartnerActions.FieldList)
    getFieldList(ctx: StateContext<PartnerStateModel>, {filters}: PartnerActions.FieldList) {
        return this.partnerService.fetchSimpleList(filters).pipe(
            tap((items: PartnerIdentity[]) => {
                ctx.patchState({
                    fieldList: items
                });
            })
        );
    }

    @Action(PartnerActions.Clear)
    localizationClear(ctx: StateContext<PartnerStateModel>) {
        ctx.patchState({
            activePartner: null
        });
    }

    @Action(PartnerActions.ListClear)
    localizationListClear(ctx: StateContext<PartnerStateModel>) {
        ctx.patchState({
            list: new SearchList<Partner>({
                filters: new FormState(),
            }),
        });
    }
}
